import WebMidi from 'webmidi';
import MidiMappable from './MidiMappable.js';
import MidiControllable from './MidiControllable.js';
import AbstractMidi from './AbstractMidi.js';
import Tone from 'tone';
import Notes from 'midi/Notes.js';
import MidiCC from './MidiCC.js';
import SliderMidiCC from './SliderMidiCC.js';
import LaunchPad2 from './LaunchPad2.js';
import {setParameterMidiMapping} from 'actions/index.js';
import {setActionMidiMapping} from 'actions/index.js';
import {deleteMidiMappings} from 'actions/index.js';
import {MAP, DELETE} from 'constants/midi-map-modes.js';
import {DEVICE_TO_SLIDER_CONFIG} from 'constants/midi-cc-interfaces.js';
import {getVolume} from 'utils/getVolume.js';
import {
    PADS,
    KEYBOARD,
    CONTROLCHANGE_SUSTAIN,
    CONTROLCHANGE_PLAY, CONTROLCHANGE_STOP,
    CONTROLCHANGE_RECORD, CONTROLCHANGE_NEXT,
    CONTROLCHANGE_PREVIOUS,
    CONTROLCHANGE_MODULATION,
} from './constants.js';

const launchPads = ['Launchpad Mini'];
const appleMidi = ['IAC Driver Bus 1', 'IAC Driver Bus 2', 'IAC Driver Bus 3'];
const knownNames = ['Akai MPD26 Port 1', 'QUNEO', 'Samson Carbon49 ', 'KOMPLETE KONTROL M32'];
const knownModes = {'Akai MPD26 Port 1': PADS, 'MPD218 Port A': PADS};
const middleCNoteNumber = 60;

// midi latency (todo: configure)
const latency = 0;

const outputLatency = 20;

class Midi extends MidiControllable(MidiMappable(AbstractMidi)) {
    constructor(keyboardEngine, keyboardHelper, updateBpm) {
        super();
        let self = this;

        this.keyboardEngine = keyboardEngine;
        this.sequencerEngine = this.keyboardEngine.sequencerEngine;
        this.keyboardHelper = keyboardHelper;

        this.deviceToMidiCC = {};
        this.sliders = {};
        
        // list of "tracked"  midi input devices, that can be used
        // as midi controllers 
        this.controllerInputs = [];

        // outputs that can be controller by the sequencer
        this.controllerOutputs = [];

        // input name -> what mode of midi it is (either keyboard or pad)
        this.inputToMode = {};

        // variables for syncing bpm 
        this.updateBpm = updateBpm;
        this.previousTimestamp = null;
        this.currentTimestamp = null;
        
        if (!window.isNFT) {
            WebMidi.enable((err) => {
                if (err) {
                    console.log("WebMidi could not be enabled.", err);
                } else {
                    this.setup();
                }
            }, true);
        }
    }

    async setup() {
        await this.loadAvailableDevices();
        this.setupKeyboardRouting();
        if (this.onEnableCallback !== undefined) {
            this.onEnableCallback();
        }
    }

    get store() {
        return this.sequencerEngine.store;
    }

    get state() {
        return this.store.getState();
    }

    async loadAvailableDevices() {
        return new Promise((resolve, reject) => {
            let connectedMidiControllerInputs = WebMidi.inputs.filter(
                input => 
                    !launchPads.some(name => name === input.name) &&
                    !appleMidi.some(name => name === input.name));
            
            connectedMidiControllerInputs.forEach(
                input => this.trackInput(input, knownModes[input.name] || KEYBOARD));

            let launchPadInputs = WebMidi.inputs.filter(
                input => 
                    launchPads.some(name => name === input.name));

            let launchPadOutputs = WebMidi.outputs.filter(
                input => 
                    launchPads.some(name => name === input.name));

            if (launchPadInputs.length > 0 && this.launchPad === undefined) {
                this.launchPad = new LaunchPad2(
                    this.sequencerEngine, launchPadInputs[0], launchPadOutputs[0]);
            }
            
            WebMidi.outputs.map(output => this.trackOutput(output));
            
            resolve();
        });
    }

    // callbacks for starting and stopping playback, upon receiving
    // start and stop midi messages
    setStartCallback(startCallback) {
        this.startCallback = startCallback;
    }

    setStopCallback(stopCallback) {
        this.stopCallback= stopCallback;
    }

    getInputs() {
        return WebMidi.inputs;
    }

    // following are functions devoted to midi output

    getOutputs() {
        return WebMidi.outputs;
    }

    getTrackedInputs() {
        return this.controllerInputs;
    }

    getTrackedOutputs() {
        return this.controllerOutputs;
    }

    isOutputTracked(output) {
        return this.controllerOutputs.some(
            o => o.name == output.name);
    }

    getOutput(name) {
        let output = this.controllerOutputs.filter(
            o => o.name == output.name);

        if (output.length > 0) {
            return output[0];
        }

        return null;
    }

    // time and duration are in milliseconds
    // offset is the transpose value (so 0 is c3 and 1 is c#3)
    // release is between 0 and 1
    playNote(output, midiChannel, transpose, time, duration, release, velocity=1) {
        midiChannel = midiChannel || 'all';

        transpose = Math.round(transpose);
        if (transpose > 70) {
            transpose = 70;
        } else if (transpose < -36) {
            transpose = -36;
        }
        let note = Notes.fromTranspose(transpose).toString();
        if (output == null) {
            // error
            return;
        }

        time -= latency;

        output.playNote(note, midiChannel, {
            velocity: velocity,
            time: '+' + time
        });
        if (duration !== undefined) {
            let stopTime = time + duration - 20;
            if (stopTime - time < 10) {
                stopTime += (10 - (stopTime - time));
            }
            output.stopNote(note, midiChannel, {
                time: '+' + stopTime,
                release: release});
        }
    }

    // note is in midi note format like C4
    playNoteSimple(output, note, midiChannel, release) {
        midiChannel = midiChannel || 'all';
        
        output.playNote(note, midiChannel, {
            release: release});
    }

    releaseNoteSimple(output, note, midiChannel, release) {
        midiChannel = midiChannel || 'all';
        output.stopNote(note, midiChannel, {
            release: release});
    }

    trackOutput(output) {
        for (let i in this.controllerOutputs) {
            if (this.controllerOutputs[i].name === output.name) {
                return;
            }
        }
        this.controllerOutputs.push(output);

        if (this.deviceToMidiCC[output.name] === undefined) {
            this.deviceToMidiCC[output.name] = new MidiCC(this.sequencerEngine);
        }

        this.deviceToMidiCC[output.name].output = output;
    }

    untrackOutput(output) {
        let index = -1;
        for (var i=0; i < this.controllerOutputs.length; i++) {
            if (this.controllerOutputs[i].name === output.name) {
                index = i;
                break;
            }
        }

        if (index > -1) {
            this.controllerOutputs.splice(index, 1);
            delete this.inputToMode[output.name];
        }
    }

    trackInput(input, mode=KEYBOARD) {
        this.addControlChangeListeners(input);

        if (DEVICE_TO_SLIDER_CONFIG[input.name] && this.sliders[input.name] === undefined) {
            let config = DEVICE_TO_SLIDER_CONFIG[input.name];
            this.sliders[input.name] = new SliderMidiCC(
                this.sequencerEngine,
                config,
                this.deviceToMidiCC[input.name]);

            // return;
        }

        for (let i in this.controllerInputs) {
            if (this.controllerInputs[i].name === input.name) {
                return;
            }
        }
        this.controllerInputs.push(input);

        // by default this input is in pad mode
        this.setInputMode(input, mode);
    }

    isInputTracked(input) {
        return this.controllerInputs.some(
            i => i.name == input.name);
    }

    isInputSynced(input) {
        return this.systemInput !== undefined && this.systemInput.name == input.name;
    }

    untrackInput(input) {
        let index = -1;
        for (var i=0; i < this.controllerInputs.length; i++) {
            if (this.controllerInputs[i].name === input.name) {
                index = i;
                break;
            }
        }

        if (index > -1) {
            this.controllerInputs.splice(index, 1);
            delete this.inputToMode[input.name];
        }
    }

    // sets the mode of an an input to either pad or keyboard
    setInputMode(input, inputMode) {
        this.inputToMode[input.name] = inputMode;
    }

    syncOutput(output) {
        if (this.systemOutput === output) {
            this.systemOutput = undefined;
        } else {
           this.systemOutput = output;
        }
    }

    onTick(time, step, resolution) {
        if (this.systemOutput !== undefined) {
            let timeDivision = new Tone.Time('32n').toMilliseconds() / 3;
            time += outputLatency;
            for (let i=0; i < 3; i++) {
                this.systemOutput.sendClock({time: '+' + time});
                time  += timeDivision;
            }
        }

        if (this.launchPad) {
            this.launchPad.onTick(step, resolution);
        }
    }
    
    onPlay() {
        if (this.systemOutput !== undefined) {
            this.systemOutput.sendStart({time: '+0'});
        }
    }

    onStop() {
        if (this.systemOutput !== undefined) {
            this.systemOutput.sendStop();
        }
    }
    
    syncInput(input) {
        if (this.systemInput !== undefined) {
            this.removeSystemListeners();
        }
        this.systemInput = input;
        this.addSystemInputListeners();
    }

    unsyncInput(input) {
        this.removeSystemListeners();
        this.systemInput = undefined;
    }

    removeSystemListeners() {
        this.systemInput.removeListener('clock', 'all');
        this.systemInput.removeListener('start', 'all');
        this.systemInput.removeListener('stop', 'all');
    }

    addSystemInputListeners() {
        let self = this;
        this.last10 = [];

        /*
        this.systemInput.addListener('clock', 'all',
           (e) =>{
               if (!this.sequencerEngine.playing) {
                   return;
               }
               // determine the bpm based on the ticks (i.e. when this is triggered)
               this.previousTimestamp = this.currentTimestamp;
               this.currentTimestamp = e.timestamp;

               if (this.previousTimestamp != null) {
                   let interval =  1000*(this.currentTimestamp - this.previousTimestamp);
                   let bpm = (1000000 / interval / 24) * 60;
                   if (bpm > 20) {
                       //self.updateBpm(bpm, true); 
                   }
               }
           });
           */

        this.systemInput.addListener('start', 'all',
            (e) => {
                this.sequencerEngine.play(0, true);
            });

        this.systemInput.addListener('stop', 'all',
            (e) => {
                this.sequencerEngine.stop();
            });
    }

    onEnable(onEnable) {
        this.onEnableCallback = onEnable;
    }

    addControlChangeListeners(input) {
        input.removeListener('pitchbend', 'all');
        input.removeListener('controlchange', 'all');
        if (this.deviceToMidiCC[input.name] === undefined) {
            this.deviceToMidiCC[input.name] = new MidiCC(this.sequencerEngine);
        }

        let name = input.name;
        let midiCC = this.deviceToMidiCC[input.name];
        midiCC.input = input;
        input.addListener('controlchange', 'all', (e) => {
            let slider = this.sliders[name];
            this.onControlChange(midiCC, slider, e);
       });

        input.addListener('pitchbend', 'all', (e) => {
            this.onPitchBend(e);
        });
    }

   removeListeners(input) {
        input.removeListener('noteon', 'all');
        input.removeListener('noteoff', 'all');
    }

    getInputsForMode(mode) {
        return this.controllerInputs.filter(
            input => input.name in this.inputToMode &&
                this.inputToMode[input.name] == mode);
    }

    getInputMode(inputName) {
        return this.inputToMode[inputName] || KEYBOARD;
    }

    setupKeyboardRouting() {
        WebMidi.inputs.forEach(input => this.removeListeners(input));

        this.addNoteOnListener((name, message, velocity) => {
            if (velocity < 0.1) {
                // skip really low velocities since they are probably
                // unintended from shhitty midi interfaces like mine
                return;
            }
            window.triggerReceived = new Date().getTime();
            velocity = Math.min(1, velocity + 0.1);
            this.keyboardHelper.onNoteDown(message, true, name, velocity);
        }, undefined);
        this.addNoteOffListener((name, message) => {
            this.keyboardHelper.onNoteOff(message, true, name);
        }, undefined);
    }

    addNoteOnListener(callback, inputsGetter) {
        if (inputsGetter === undefined) {
            inputsGetter = () => this.controllerInputs;
        }

        let self = this;
        WebMidi.inputs
            .map(
                input =>
                    input.addListener(
                        'noteon', "all",
                        (e) => {
                            let slider = this.sliders[input.name];
                            if (slider) {
                                if (slider.onNoteOnEvent(e)) {
                                    return;
                                }
                            }

                            if (inputsGetter().some(i => i.name == input.name)) {
                                let message = e.note.name + e.note.octave;
                                callback(input.name, message, e.velocity);
                            }
                        }));
    }

    addNoteOffListener(callback, inputsGetter) {
        if (inputsGetter === undefined) {
            inputsGetter = () => this.controllerInputs;
        }
        let self = this;
        WebMidi.inputs.map(
            input =>
                input.addListener(
                    'noteoff', "all",
                    (e) => {
                        let slider = this.sliders[input.name];
                        if (slider) {
                            if (slider.onNoteOffEvent(e)) {
                                return;
                            }
                        }

                        if (inputsGetter().some(i => i.name == input.name)) {
                            let message = e.note.name + e.note.octave;
                            callback(input.name, message);
                        }
                    }));
    }

    updateSteps(steps) {
    }
}

export default Midi;
